import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { User, Lock, Mail, Shield, Eye, EyeOff, Check, Phone, Building2, Save, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountSettingsPage() {
  const { user, token, fetchMe, setPendingVerification } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const hasPassword = user?.has_password;

  // Password state
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile edit state
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    personal_phone: user?.personal_phone || '',
    brokerage_name: user?.brokerage_name || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const valid = pw.length >= 8 && pw === confirm;

  const handleSetPassword = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await axios.post(`${API}/auth/set-password`, { password: pw }, { headers });
      await fetchMe(token);
      toast.success(hasPassword ? 'Password updated' : 'Password set! You can now sign in with email & password.');
      setPw('');
      setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setProfileForm({
      full_name: user?.full_name || '',
      email: user?.email || '',
      personal_phone: user?.personal_phone || '',
      brokerage_name: user?.brokerage_name || '',
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const handleSaveProfile = async () => {
    // Route through unified-auth so a phone-number change triggers OTP re-verification.
    const payload = {};
    if (profileForm.full_name !== (user?.full_name || '')) payload.full_name = profileForm.full_name;
    if (profileForm.email !== (user?.email || '')) payload.email = profileForm.email;
    if (profileForm.personal_phone !== (user?.personal_phone || '')) payload.phone = profileForm.personal_phone;
    if (profileForm.brokerage_name !== (user?.brokerage_name || '')) payload.company_name = profileForm.brokerage_name;

    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }

    setSavingProfile(true);
    try {
      const { data } = await axios.put(`${API}/unified-auth/profile`, payload, { headers });

      // Phone number changed → must re-verify the new number via OTP.
      if (data.phone_verification_required) {
        setPendingVerification({
          phone: data.phone,
          masked_phone: data.masked_phone,
          products: data.user?.products || user?.products || ['agent9'],
          user_id: data.user?.id || user?.id,
        });
        setEditing(false);
        toast.info(`Verify your new phone number — code sent to ${data.masked_phone}`);
        navigate('/signup/verify');
        return;
      }

      await fetchMe(token);
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-5" data-testid="account-settings-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-foreground">Account</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account details and security</p>
      </div>

      {/* Profile Info Card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <User className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <CardTitle className="text-foreground text-base">Profile</CardTitle>
                <CardDescription>Your account information</CardDescription>
              </div>
            </div>
            {!editing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={startEditing}
                className="text-muted-foreground hover:text-foreground hover:bg-muted"
                data-testid="edit-profile-btn"
              >
                <Pencil size={14} className="mr-1.5" />
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!editing ? (
            <>
              <div className="flex items-center gap-4">
                {user?.profile_picture ? (
                  <img src={user.profile_picture} alt="" className="h-14 w-14 rounded-full object-cover border-2 border-border" referrerPolicy="no-referrer" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-emerald-500/20 border-2 border-border flex items-center justify-center">
                    <span className="text-lg font-bold text-emerald-400">{(user?.full_name || '?').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div className="space-y-0.5">
                  <p className="text-foreground font-medium" data-testid="account-name">{user?.full_name || 'User'}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Mail size={12} /> {user?.email}
                  </p>
                  {user?.personal_phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="account-phone">
                      <Phone size={12} /> {user.personal_phone}
                    </p>
                  )}
                  {user?.brokerage_name && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="account-brokerage">
                      <Building2 size={12} /> {user.brokerage_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                {user?.profile_picture && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 text-blue-400 text-[11px] font-medium">
                    <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
                    Google connected
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium ${hasPassword ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`} data-testid="password-status">
                  {hasPassword ? <><Check size={10} /> Password set</> : <><Lock size={10} /> No password</>}
                </span>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-foreground">Full Name</Label>
                <Input
                  data-testid="edit-name-input"
                  value={profileForm.full_name}
                  onChange={e => setProfileForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Your full name"
                  className="mt-1.5 bg-muted border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <Label className="text-foreground">Email</Label>
                <Input
                  data-testid="edit-email-input"
                  type="email"
                  value={profileForm.email}
                  onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="mt-1.5 bg-muted border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <Label className="text-foreground">Phone Number</Label>
                <Input
                  data-testid="edit-phone-input"
                  type="tel"
                  value={profileForm.personal_phone}
                  onChange={e => setProfileForm(p => ({ ...p, personal_phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                  className="mt-1.5 bg-muted border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <Label className="text-foreground">Brokerage Name</Label>
                <Input
                  data-testid="edit-brokerage-input"
                  value={profileForm.brokerage_name}
                  onChange={e => setProfileForm(p => ({ ...p, brokerage_name: e.target.value }))}
                  placeholder="Your brokerage"
                  className="mt-1.5 bg-muted border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="save-profile-btn"
                >
                  <Save size={14} className="mr-1.5" />
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={cancelEditing}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Set/Change Password Card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-foreground text-base">{hasPassword ? 'Change Password' : 'Set Password'}</CardTitle>
              <CardDescription>
                {hasPassword
                  ? 'Update your password for email login'
                  : 'Set a password so you can also sign in with email & password'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-foreground">New Password</Label>
            <div className="relative mt-1.5">
              <Input
                data-testid="set-password-input"
                type={showPw ? 'text' : 'password'}
                value={pw}
                onChange={e => setPw(e.target.value)}
                placeholder="Min 8 characters"
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground pr-10"
              />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-foreground">Confirm Password</Label>
            <Input
              data-testid="confirm-password-input"
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              className="mt-1.5 bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
            {confirm && pw !== confirm && (
              <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
            )}
          </div>

          <Button
            onClick={handleSetPassword}
            disabled={!valid || saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="set-password-btn"
          >
            <Lock size={16} />
            {saving ? 'Saving...' : hasPassword ? 'Update Password' : 'Set Password'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
